<template>
  <div class="collection-filter container" ref="filter-root">
    <item-detail :value="selectedItem" :showModal="showModal" @close="showModal = false" />

    <div class="filter-row sticky-top p-3 bg-white border rounded">
      <div class="col-sm pl-0 pr-0 pr-sm-2">
        <search-filter v-model="searchFilterValue" :placeholder="searchPlaceholder" />
      </div>
      <div class="col-sm pl-0 pr-0 pr-sm-2">
        <museum-filter
          v-model="museumFilterValue"
          :initialId="initialMuseumId"
          :filterData="museumFilterData"
          :placeholder="museumPlaceholder"
        />
      </div>
      <div class="col-sm pl-0 pr-0 pr-sm-2">
        <maker-filter
          v-model="makerFilterValue"
          :filterData="makerFilterData"
          :placeholder="makerPlaceholder"
        />
      </div>
    </div>

    <div v-if="loading" class="mt-5 d-flex justify-content-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <template v-if="queryResult">
      <div v-if="queryResult.data.count">
        <div class="m-3 d-flex justify-content-center">
          <span>{{ queryResult.data.count }} gevonden collectie items</span>
        </div>
      </div>
      <div v-if="!queryResult.data.count">
        <div class="mt-5 d-flex justify-content-center">Geen items gevonden</div>
      </div>

      <div class="card-deck">
        <div
          class="content-item card"
          v-for="item in queryResult.data.results"
          :key="`item-${item.id}`"
        >
          <a href v-on:click.prevent="showItem(item)">
            <template v-if="item.collection_images.length">
              <img class="card-img-top" :src="item.collection_images[0].thumb.url" alt />
              <!-- <img
                data-sizes="auto"
                :data-src="item.collection_images[0].thumb.url"
                :data-srcset="item.collection_images[0].srcset"
                class="card-img-top lazyload" /> -->
            </template>
            <template v-if="!(item.collection_images.length)">geen foto</template>
          </a>

          <div class="card-body">
            <h5 class="card-title">{{ item.name }}</h5>
            <p class="card-text">{{ item.museum.title }}</p>
          </div>
        </div>
      </div>

      <infinite-loading
        v-if="queryResult.data.count"
        @infinite="infiniteHandler"
        :identifier="queryResult"
        :distance="2000"
      >
        <template v-slot:spinner>
          <div class="mt-0 d-flex justify-content-center">
            <div class="spinner-border" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </template>
        <template v-slot:nomore>
          <div class="mt-0 d-flex justify-content-center">
            <span>Einde van de resultaten</span>
          </div>
        </template>
      </infinite-loading>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import throttle from 'lodash.throttle';
// import 'lazysizes'; // pikt juiste breedte voor img srcset
// import 'lazysizes/plugins/attrchange/ls.attrchange'; // nodig na filtering

import InfiniteLoading from './InfiniteLoading.vue';
import MuseumFilter from './MuseumFilter.vue';
import MakerFilter from './MakerFilter.vue';
import SearchFilter from './SearchFilter.vue';
import ItemDetail from './ItemDetail.vue';

export default {
  name: 'CollectionFilter',
  components: {
    MuseumFilter,
    MakerFilter,
    SearchFilter,
    ItemDetail,
    InfiniteLoading,
  },
  data() {
    return {
      baseUrl: '',
      museumFilterData: null,
      museumFilterValue: null,
      makerFilterData: null,
      makerFilterValue: null,
      initialMuseumId: null,
      searchFilterValue: null,
      queryResult: null,
      loading: 0,
      currentPage: 1,
      selectedItem: null,
      showModal: null,
      filterRoot: null,
      cancelToken: null,
    };
  },
  props: {
    museumPlaceholder: String,
    makerPlaceholder: String,
    searchPlaceholder: String,
    prevLabel: String,
    nextLabel: String,
  },
  mounted() {
    // this fixes relative links when embedded in plone
    // const canonicalLink = document.querySelector('link[rel=canonical]');
    // if (canonicalLink) {
    //   this.baseUrl = canonicalLink.href;
    // }
    this.filterRoot = this.$refs['filter-root'];

    const linkElement = document.createElement('link');

    /* add attributes */
    linkElement.setAttribute('rel', 'stylesheet');
    linkElement.setAttribute(
      'href',
      `${process.env.VUE_APP_STATIC_DIR}/collection-filter.css`,
    );

    /* attach to the document head */
    // document.getElementsByTagName('head')[0].appendChild(linkElement);

    this.filterRoot.appendChild(linkElement);

    const museumListUrl = `${process.env.VUE_APP_API_URL}museum/`;
    axios.get(museumListUrl).then((response) => {
      this.museumFilterData = response.data.results;
      this.loadMuseumFilterFromQueryParam();
    });

    const makerListUrl = `${process.env.VUE_APP_API_URL}maker/`;
    axios.get(makerListUrl).then((response) => {
      this.makerFilterData = response.data.results;
    });

    this.queryCollection();
    document.addEventListener('keydown', this.keyDown, false);
  },
  created() {
    // just to make sure that we limit the number of calls to our backend a littles
    this.queryCollection = throttle(this.queryCollection, 500);
  },
  destroyed() {},
  methods: {
    loadMuseumFilterFromQueryParam() {
      const params = new URLSearchParams(document.location.search);
      let museumName = params.get('museum');
      if (!museumName) {
        // old param from previous website. some sites still have this deeplink
        museumName = params.get('f_string_setting_name[0]');
      }

      // loop through this.makerFilterData until museum match is found
      const museum = this.museumFilterData.find(m => m.title === museumName);
      if (museum) {
        this.initialMuseumId = museum.id;
      }
    },
    keyDown(e) {
      if (e.keyCode === 27) {
        // for some reason the escape key doesn't work when in webcomponent. this fixes it
        this.showModal = false;
      }
    },
    infiniteHandler($state) {
      if (this.queryResult.data.total_pages <= this.currentPage) {
        $state.complete();
      } else {
        this.queryCollection($state);
      }
    },
    showItem(item) {
      this.showModal = true;
      this.selectedItem = item;
    },

    getQueryParams() {
      const params = new URLSearchParams();
      params.append('web_display', true);

      if (this.museumFilterValue) {
        this.museumFilterValue.forEach((val) => {
          params.append('museum', val.id);
        });
      }

      if (this.makerFilterValue) {
        params.append('maker', this.makerFilterValue.id);
      }

      if (this.searchFilterValue) {
        params.append('search', this.searchFilterValue);
      }

      params.append('page', this.currentPage);

      return params;
    },

    queryCollection($state) {
      if (this.cancelToken && this.currentPage === 1) {
        // cancel ongoing request if this is the first page (a complete new query)
        this.cancelToken.cancel('Only one request allowed at a time.');
      }
      this.cancelToken = axios.CancelToken.source();

      return this.doQueryCollection(this.getQueryParams(), $state);
    },

    doQueryCollection(params, $state) {
      this.loading = this.loading + 1;
      const collectionUrl = `${process.env.VUE_APP_API_URL}collection/`;
      if ($state) {
        this.currentPage = this.currentPage + 1;
        params.set('page', this.currentPage);
      }

      const qq = async () => {
        const promise = axios.get(collectionUrl, {
          params,
          cancelToken: this.cancelToken.token,
        });
        await promise
          .then((response) => {
            if ($state) {
              this.queryResult.data.results.push(...response.data.results);
              if (response.data.total_pages > this.currentPage) {
                $state.loaded();
              } else {
                $state.complete();
              }
            } else {
              this.queryResult = response;
            }
          })
          .catch((error) => {
            // error callback
            if (axios.isCancel(error)) {
              // no worries
            } else {
              // handle error
              $state.error();
              throw error.message;
            }
          })
          .then(() => {
            this.loading = this.loading - 1;
          });
      };
      qq();
    },
    doFilter() {
      this.currentPage = 1;
      this.queryResult = null; // will clear the result and sideeffect scrollbar resets
      this.queryCollection();
    },
  },
  watch: {
    museumFilterValue() {
      this.doFilter();
    },
    makerFilterValue() {
      this.doFilter();
    },
    searchFilterValue() {
      this.doFilter();
    },
  },
};
</script>

<style lang="scss">
@import "node_modules/vue-select/src/scss/vue-select.scss";

.filter-row {
  display: flex;
}

</style>
